/**
 * Live re-reads for Meeting Detail → Review (#3820) — a THIN SELECTOR over the
 * shared realtime bus (`src/realtime/`).
 *
 * A review is built for the person reading it (whose response decides, their
 * own verdicts, what they may still do), so no review ever arrives over the
 * socket. Two things do, and both are answered the same way: the open page
 * re-reads ITS OWN view, as itself.
 *
 *  1. `meeting-reviews review-changed` (`meetingReviewChannel`) — a response or
 *     an answer recorded, the window opening, a close pass deciding. The API
 *     sends it only to the people who may open the review (and, for a private
 *     meeting, only its members and administrators) with the meeting's id and
 *     nothing else, so arriving at all means "you may read this; read it".
 *  2. The Meeting Intelligence events about this meeting that the Action Items
 *     tab already follows (`isAboutMeeting`): the analysis landing, an item
 *     closed or reopened, an assignment of one of its items accepted, rejected,
 *     completed or moved to another module. Each already reaches only people
 *     who may see the meeting. A person the review turned away (403) does not
 *     re-read on these — only the signal, which is addressed to its readers.
 *
 * A burst is one re-read (`coalesce`): a close pass accepts several items and
 * says so once more besides.
 */
import type RootStore from "stores/RootStore";
import { meetingReviewChannel, subscribeRealtime } from "realtime";
import { coalesce } from "shared/coalesce";
import { isAboutMeeting } from "../meetingEventScope";
import { subscribeMeetingIntelEvents } from "../meetingIntelligenceRealtime";

/** How long a burst of review events is gathered into one re-read. */
export const REVIEW_LIVE_DEBOUNCE_MS = 250;

/** What the page has open, asked afresh on every event. */
export interface WatchedReview {
  meetingId: number | null;
  /** The assignments of the review's items, as strings. */
  assignmentIds: ReadonlySet<string>;
  /** False once the review refused this person: only the signal is worth a re-read then. */
  mayOpen: boolean;
}

/**
 * Call `reread` once per burst of events that change the open review. Returns
 * an idempotent disposer, which also drops a re-read still waiting to run.
 */
export function watchMeetingReview(
  rootStore: RootStore,
  watched: () => WatchedReview,
  reread: () => void,
  debounceMs: number = REVIEW_LIVE_DEBOUNCE_MS
): () => void {
  const soon = coalesce(reread, debounceMs);
  const offSignal = subscribeRealtime(rootStore, meetingReviewChannel, (event) => {
    const { meetingId } = watched();
    if (meetingId != null && event.meetingId === meetingId) soon();
  });
  const offIntel = subscribeMeetingIntelEvents(rootStore, (event) => {
    const { meetingId, assignmentIds, mayOpen } = watched();
    if (meetingId == null || !mayOpen) return;
    if (isAboutMeeting(event, meetingId, assignmentIds)) soon();
  });
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    soon.cancel();
    offSignal();
    offIntel();
  };
}
